import { computed } from 'vue'

import { useRoute } from 'nuxt/app'

import { useI18n } from 'vue-i18n'

import { useQueryClient } from '@tanstack/vue-query'

import { resolveCpBreadcrumbRegistryKey } from '@common/navigation-layer/config/cp-breadcrumb-registry'

import { useGroups } from '@communities/groups-layer/composables/useGroups'

import { resolveGroupNameFromCache } from '@communities/groups-layer/utils/resolve-group-name-from-cache'

import type { CpCommunitiesGroupBreadcrumbContext } from '@common/navigation-layer/types/cp-breadcrumb'

/**
 * Supplies group name + slug for `useCPBreadcrumbs` on `communities-groups-*` routes.
 *
 * Resolution order for the group name:
 *  1. `groupsStore.currentGroup` when its slug matches (fastest — already in Pinia)
 *  2. TanStack Query groups-list cache (populated by the sidebar) — avoids showing
 *     "Loading" during the window when `useGroupInitialization` resets the store on
 *     slug change but before the detail query re-populates it
 *  3. Fallback: `t('common.loading')`
 */
export function useCPCommunitiesGroupBreadcrumbContext() {
  const route = useRoute()
  const { t } = useI18n()
  const { store } = useGroups()
  const queryClient = useQueryClient()

  return computed((): CpCommunitiesGroupBreadcrumbContext | null => {
    const key = resolveCpBreadcrumbRegistryKey(route)
    if (!key?.startsWith('communities-groups-')) {
      return null
    }
    const groupSlug = route.params.groupSlug as string | undefined
    if (!groupSlug) {
      return null
    }

    // 1. Pinia store — set when group detail has fully loaded
    const current = store.currentGroup
    if (current && current.slug === groupSlug) {
      const name = current.name?.trim()
      if (name && name.length > 0) {
        return { groupSlug, groupName: name, groupId: current._id }
      }
    }

    // 2. Groups-list / groupBySlug TanStack Query caches — populated by sidebar or detail fetch.
    // Client-only: read-only name resolution via getQueryData (no subscription); SSR has no
    // hydrated TanStack cache here, so we skip this path and fall through to i18n loading / store.
    if (!process.server) {
      const cached = resolveGroupNameFromCache(queryClient, groupSlug)
      if (cached) {
        return { groupSlug, groupName: cached.name, groupId: cached.groupId }
      }
    }

    return { groupSlug, groupName: t('common.loading') }
  })
}
